<template>
  <div>
    <div class="prescription-meta">
      <div class="prescription-meta-items">
        <span v-if="data.doctorName" class="meta-chip">
          <span class="meta-label">{{ $t('visit.prescriptionDoctor') }}</span>
          <span>{{ data.doctorName }}</span>
        </span>
        <span v-if="data.prescriptionType" class="meta-chip">
          <span class="meta-label">{{ $t('visit.prescriptionType') }}</span>
          <span>{{ data.prescriptionType }}</span>
        </span>
        <span v-if="data.code" class="meta-chip meta-chip-code">
          <span class="meta-label">{{ $t('visit.code') }}</span>
          <span class="meta-value">{{ data.code }}</span>
        </span>
        <span v-if="formatAdditionalRights(data)" class="meta-chip">
          <span class="meta-label">{{ $t('visit.additionalRights') }}</span>
          <span>{{ formatAdditionalRights(data) }}</span>
        </span>
      </div>
    </div>
    <b-card
      v-for="el in data.medicines"
      :key="`medicine${el.id}`"
      class="prescription-medicine-card"
    >
      <b-row>
        <b-col cols="12">
          <strong>{{ el.name || el.partName }}</strong>
          <span v-if="el.form || el.strength" class="medicine-subtitle">
            {{ [el.form, el.strength].filter(Boolean).join(', ') }}
          </span>
        </b-col>
        <b-col cols="12" md="6">
          <span>{{ formatPackage(el) }}</span>
        </b-col>
        <b-col cols="12" md="6">
          <span>{{ $t('visit.refundation') }}: {{ el.refund }}</span>
        </b-col>
        <b-col cols="12" md="6" v-if="el.displayEan || el.ean" class="mt-1">
          <small class="text-muted">
            {{ $t('visit.ean') }}: {{ el.displayEan || el.ean }}
          </small>
        </b-col>
        <b-col cols="12" md="6" v-if="el.packageCode" class="mt-1">
          <small class="text-muted">
            {{ $t('visit.packageCode') }}: {{ el.packageCode }}
          </small>
        </b-col>
        <b-col cols="12" class="mt-1">
          <span>
            {{ $t('visit.dosing') }}: {{ el.dose }}
          </span>
        </b-col>
        <b-col cols="12" v-if="el.refundIndication" class="mt-1">
          <small class="text-muted">
            {{ $t('visit.refundIndication') }}: {{ el.refundIndication }}
          </small>
        </b-col>
        <b-col cols="12" v-if="formatAdditionalRights(el)" class="mt-1">
          <small class="text-muted">
            {{ $t('visit.additionalRights') }}: {{ formatAdditionalRights(el) }}
          </small>
        </b-col>
        <b-col cols="12" v-if="hasExtendedDosage(el)" class="mt-2">
          <div class="extended-dosage-title">
            {{ $t('visit.extendedDosage') }}
          </div>
          <div
            v-for="(dosing, index) in el.extendedDosage"
            :key="`extended-dosage-${el.id}-${index}`"
            class="extended-dosage-row"
          >
            {{ formatExtendedDosage(dosing) }}
          </div>
        </b-col>
        <b-col cols="12" v-if="el.dateFrom || el.dateTo" class="mt-2">
          <small class="text-muted">
            {{ $t('visit.prescriptionRealization') }}:
            {{ [formatDate(el.dateFrom), formatDate(el.dateTo)].filter(Boolean).join(' - ') }}
          </small>
        </b-col>
        <b-col cols="12" v-if="el.notReplace" class="mt-1">
          <small class="text-muted">{{ $t('visit.notReplace') }}</small>
        </b-col>
        <b-col cols="12" v-if="el.isStrong" class="mt-2">
          <div class="strong-medicine">
            {{ $t('visit.strongMedicamentAlert') }}
          </div>
        </b-col>
        <b-col cols="12" v-if="hasSubstances(el)" class="mt-2">
          <div class="extended-dosage-title">
            {{ $t('visit.substances') }}
          </div>
          <div
            v-for="(substance, index) in el.substances"
            :key="`substance-${el.id}-${index}`"
            class="substance-row"
          >
            {{ formatSubstance(substance, el) }}
          </div>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>
<script>
import moment from 'moment'

export default {
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  methods: {
    hasExtendedDosage(medicine) {
      return (
        Array.isArray(medicine.extendedDosage) &&
        medicine.extendedDosage.length > 0
      )
    },
    formatExtendedDosage(dosing) {
      const frequency = dosing.frequency || 0
      const duration = dosing.duration || 0
      const dose = dosing.dosing || 0
      return `${this.$t('visit.every')} ${frequency} ${this.$t('visit.days')}, ${this.$t('visit.dosageFor')} ${duration} ${this.$t('visit.days')}, ${dose} ${this.$t('visit.dose').toLowerCase()}`
    },
    formatPackage(medicine) {
      const parts = [
        medicine.quantity,
        medicine.packageDescription || 'op. po',
        medicine.package
      ].filter(Boolean)
      return parts.join(' ')
    },
    formatAdditionalRights(data) {
      return [data.additionalRights, data.additionalRightsDisplayName]
        .filter(Boolean)
        .join(' - ')
    },
    hasSubstances(medicine) {
      return Array.isArray(medicine.substances) && medicine.substances.length > 0
    },
    formatSubstance(substance, medicine) {
      const name = substance.substanceName || substance.name
      const unit = substance.substanceUnit || substance.unit
      // recepta strong: na recepcie w P1 jest pole × wielkość opakowania (tak liczy integrator);
      // po poprawce RECEPTY_STRONG_FIX pole niesie już dawkę × liczbę opakowań — pokazujemy sumę z recepty
      const total = this.confirmedSubstanceTotal(substance.substanceQtyPerUnit, medicine && medicine.package)
      if (total !== null) {
        return [name, total, unit, this.$t('visit.substanceTotal')].filter(Boolean).join(' ')
      }
      const quantity =
        substance.substanceQtyPerUnit ||
        substance.substanceQty ||
        substance.value
      return [name, quantity, unit].filter(Boolean).join(' ')
    },
    confirmedSubstanceTotal(perUnit, packageSize) {
      const pojemnosc = String(packageSize || '').replace(/\D/g, '')
      if (!/^\s*\d+(\.\d+)?\s*$/.test(String(perUnit || '')) || !pojemnosc) return null
      return String(Number((Number(perUnit) * Number(pojemnosc)).toFixed(3)))
    },
    formatDate(date) {
      return date ? moment(date).format('YYYY-MM-DD') : ''
    }
  }
}
</script>
<style scoped>
.prescription-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.prescription-meta-items {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 8px;
}
.meta-chip {
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  color: #343a40;
  display: inline-flex;
  gap: 6px;
  min-height: 36px;
  padding: 7px 10px;
}
.meta-chip-code {
  background: #eaf7f8;
  border-color: rgba(8, 155, 171, 0.18);
}
.meta-label {
  color: #6c757d;
  font-size: 12px;
  font-weight: 700;
}
.meta-value {
  color: #075f68;
  font-weight: 700;
}
.prescription-medicine-card {
  margin-bottom: 8px;
}
.medicine-subtitle {
  color: #6c757d;
  display: block;
  font-size: 13px;
  margin-top: 2px;
}
.extended-dosage-title {
  color: #495057;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 3px;
}
.extended-dosage-row {
  background: #f8f9fa;
  border-left: 3px solid #089bab;
  font-size: 13px;
  margin-bottom: 3px;
  padding: 4px 8px;
}
.strong-medicine {
  color: #dc3545;
  font-size: 13px;
  font-weight: 700;
}
.substance-row {
  background: #f8f9fa;
  border-left: 3px solid #6c757d;
  font-size: 13px;
  margin-bottom: 3px;
  padding: 4px 8px;
}
</style>
